import { apiClient } from './client';

export interface DashboardMetrics {
  summary: {
    totalFirms: number;
    totalAgents: number;
    onlineAgents: number;
    totalDataSources: number;
    totalApiKeys: number;
    syncsLast24h: number;
    successfulSyncs: number;
    failedSyncs: number;
    totalRowsSynced: number;
    errorLogsLast24h: number;
    syncSuccessRate: number;
  };
  agentStatus: {
    online: number;
    offline: number;
  };
  recentSyncs: SyncRecord[];
  syncTrend: SyncTrendItem[];
}

export interface SyncRecord {
  id: number;
  firmId: number;
  firmName: string;
  dataSourceName: string;
  status: string;
  rowsFetched: number;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
}

export interface SyncTrendItem {
  date: string;
  total: number;
  success: number;
  failed: number;
  rowsSynced: number;
}

export interface FirmMetrics {
  firm: {
    id: number;
    name: string;
    subdomain: string;
    isActive: boolean;
  };
  agents: {
    id: number;
    name: string;
    isOnline: boolean;
    lastSeenAt: string | null;
    isActive: boolean;
  }[];
  dataSources: {
    id: number;
    name: string;
    sourceType: string;
    isActive: boolean;
    lastSyncAt: string | null;
    syncsLast24h: number;
    lastSyncStatus: string | null;
  }[];
  syncHistory: SyncTrendItem[];
  logSummary: { level: string; count: number }[];
  apiKeyUsage: {
    id: number;
    name: string;
    isActive: boolean;
    lastUsedAt: string | null;
  }[];
}

export interface SyncHistoryResponse {
  data: SyncHistoryItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface SyncHistoryItem {
  id: number;
  firmId: number;
  firmName: string;
  dataSourceId: number;
  dataSourceName: string;
  agentId: number | null;
  agentName: string | null;
  status: string;
  rowsFetched: number;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  errorMessage: string | null;
}

export const monitoringApi = {
  getDashboard: async (): Promise<DashboardMetrics> => {
    const response = await apiClient.get('/api/monitoring/dashboard');
    return response.data;
  },

  getFirmMetrics: async (firmId: number): Promise<FirmMetrics> => {
    const response = await apiClient.get(`/api/monitoring/firms/${firmId}`);
    return response.data;
  },

  getSyncHistory: async (params?: {
    firmId?: number;
    dataSourceId?: number;
    status?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  }): Promise<SyncHistoryResponse> => {
    const response = await apiClient.get('/api/monitoring/sync-history', { params });
    return response.data;
  },
};
